import { gql } from '@apollo/client'
import { RichText, useToast } from '@farol-ds/react'
import {
  BOX_MODAL_IFRAME_URL,
  FEATURE_CODES,
  layoutStyles,
  ScrollToTopButton,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import {
  CopyContentTriggerStandaloneFarol,
  CopyRequireLoginTrigger,
} from '@jusbrasil-web/shared-feature-locker-farol'
import { COMPONENT_CLASS, FEATURE_NAME } from '@jusbrasil-web/shared-ga4-events'
import classNames from 'classnames'
import { RefObject } from 'react'

import {
  MotionDocument_DocumentFragment,
  MotionDocument_ViewerFragment,
} from './__generated__/motion-document.graphql'
import styles from './motion-document.module.scss'

export interface MotionDocumentProps {
  document: MotionDocument_DocumentFragment
  viewer: MotionDocument_ViewerFragment | null
  processedText?: string
  contentRef: RefObject<HTMLDivElement>
}

export function MotionDocument(props: MotionDocumentProps) {
  const { document, viewer, processedText, contentRef } = props
  const { metricsProperties } = useMetrics<HTMLDivElement>()
  const toast = useToast()

  const text = processedText || document.text
  if (!text) {
    return null
  }

  const featureLockProps = {
    appId: 'web-docview',
    documentOrHaystackItem: document,
    featureCode: FEATURE_CODES.COPY_PECAS_CONTENT,
    ssrEventsProperties: metricsProperties,
    viewer,
  }

  const ga4EventData = {
    componentClass: COMPONENT_CLASS.DOCUMENT_BODY_FULL_CONTENT,
    copyFeatureName: FEATURE_NAME.COPY_MOTION_CONTENT,
  }

  return (
    <CopyRequireLoginTrigger {...featureLockProps}>
      <CopyContentTriggerStandaloneFarol
        {...featureLockProps}
        eventNamespace="Peca"
        onCopySuccess={toast}
        iframeUrl={BOX_MODAL_IFRAME_URL}
        ga4EventData={ga4EventData}
      >
        <RichText ref={contentRef} asChild>
          <article
            id="MotionPage-content-paywall"
            className={classNames(layoutStyles.contentWrapper, styles.document)}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </RichText>
        <ScrollToTopButton eventNamespace="Peca" />
      </CopyContentTriggerStandaloneFarol>
    </CopyRequireLoginTrigger>
  )
}

MotionDocument.fragments = {
  document: gql`
    fragment MotionDocument_document on Document {
      text(highlight: true)

      ...CopyRequireLoginTrigger_document
      ...CopyContentTriggerStandaloneFarol_document
    }

    ${CopyRequireLoginTrigger.fragments.document}
    ${CopyContentTriggerStandaloneFarol.fragments.document}
  `,

  viewer: gql`
    fragment MotionDocument_viewer on Profile {
      ...CopyRequireLoginTrigger_viewer
      ...CopyContentTriggerStandaloneFarol_viewer
    }

    ${CopyRequireLoginTrigger.fragments.viewer}
    ${CopyContentTriggerStandaloneFarol.fragments.viewer}
  `,
}
